<template>
  <v-card class="portfolio-editor rounded-xl border border-white-05 bg-editor overflow-hidden">
    <v-toolbar flat density="compact" class="bg-surface">
      <v-toolbar-title class="text-subtitle-1 font-weight-black">Portfolio Editor</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon="mdi-close" variant="text" size="small" @click="$emit('close')"></v-btn>
    </v-toolbar>

    <v-tabs v-model="activeTab" density="compact" color="primary" grow>
      <v-tab value="bio">Bio</v-tab>
      <v-tab value="ludography">Games</v-tab>
      <v-tab value="experience">Experience</v-tab>
      <v-tab value="links">Links</v-tab>
    </v-tabs>

    <v-divider></v-divider>

    <!-- Loading/Saving Overlay -->
    <v-overlay :model-value="loading || saving" persistent class="align-center justify-center" scrim="#000000" opacity="0.85" contained style="z-index: 9999;">
      <div class="text-center pa-10">
        <v-progress-circular indeterminate color="primary" size="64" width="4"></v-progress-circular>
        <div class="mt-6">
          <p class="text-h6 font-weight-black text-white mb-1">{{ saving ? 'Uploading & saving...' : 'Loading...' }}</p>
          <p class="text-body-2 text-grey-lighten-1">Please wait, your data is being processed</p>
        </div>
      </div>
    </v-overlay>

    <v-card-text class="pa-4 overflow-y-auto" style="max-height: 70vh;">
      <v-window v-model="activeTab">
        <v-window-item value="bio" class="mt-2">
          <input
            type="file"
            ref="imageInput"
            class="d-none"
            accept="image/png,image/jpeg,image/gif,image/webp"
            @change="onImageFileSelected"
          />
          <input
            type="file"
            ref="gameImageInput"
            class="d-none"
            accept="image/png,image/jpeg,image/gif,image/webp"
            @change="onGameImageFileSelected"
          />
          <v-textarea
            v-model="internalPortfolio.bio"
            label="Short Biography"
            placeholder="Tell us about yourself..."
            variant="outlined"
            auto-grow
            rows="3"
            class="mb-6"
          ></v-textarea>

          <div class="d-flex align-center justify-space-between mb-4">
            <h4 class="text-subtitle-2 font-weight-black">Special Sections</h4>
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" prepend-icon="mdi-plus" variant="flat" color="primary" size="small" rounded="pill">Add Block</v-btn>
              </template>
              <v-list density="compact">
                <v-list-item @click="addBlock('h1')">
                  <template v-slot:prepend><v-icon icon="mdi-format-header-1" size="small"></v-icon></template>
                  <v-list-item-title>Heading 1</v-list-item-title>
                </v-list-item>
                <v-list-item @click="addBlock('h2')">
                  <template v-slot:prepend><v-icon icon="mdi-format-header-2" size="small"></v-icon></template>
                  <v-list-item-title>Heading 2</v-list-item-title>
                </v-list-item>
                <v-list-item @click="addBlock('text')">
                  <template v-slot:prepend><v-icon icon="mdi-text" size="small"></v-icon></template>
                  <v-list-item-title>Text Block</v-list-item-title>
                </v-list-item>
                <v-list-item @click="imageInput?.click()">
                  <template v-slot:prepend><v-icon icon="mdi-image" size="small"></v-icon></template>
                  <v-list-item-title>Image</v-list-item-title>
                </v-list-item>
                <v-list-item @click="addBlock('image')">
                  <template v-slot:prepend><v-icon icon="mdi-link-variant" size="small"></v-icon></template>
                  <v-list-item-title>Image Link</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <div v-for="(section, index) in internalPortfolio.sections" :key="'block-' + index + section.type" class="block-editor mb-4 pa-3 rounded-lg bg-black-05 border border-white-05">
            <div class="d-flex align-center mb-2">
              <v-chip size="x-small" color="primary" class="font-weight-black text-uppercase">{{ section.type }}</v-chip>
              <v-spacer></v-spacer>
              <v-btn icon="mdi-arrow-up" variant="text" size="x-small" @click="moveBlock(index, -1)" :disabled="index === 0"></v-btn>
              <v-btn icon="mdi-arrow-down" variant="text" size="x-small" @click="moveBlock(index, 1)" :disabled="index === internalPortfolio.sections.length - 1"></v-btn>
              <v-btn icon="mdi-delete" variant="text" size="x-small" color="error" @click="removeBlock(index)"></v-btn>
            </div>
            
            <v-text-field
              v-if="section.type.startsWith('h')"
              v-model="section.content"
              placeholder="Enter heading text..."
              variant="plain"
              hide-details
              class="text-h6 font-weight-bold"
            ></v-text-field>
            <v-textarea
              v-else-if="section.type === 'text'"
              v-model="section.content"
              placeholder="Enter content..."
              variant="plain"
              auto-grow
              hide-details
              class="text-body-2"
            ></v-textarea>
            <div v-else-if="section.type === 'image'">
              <v-img
                v-if="resolveImageUrl(section)"
                :src="resolveImageUrl(section)"
                class="rounded-lg mb-2 border border-white-05"
                style="max-height: 200px; width: auto;"
              ></v-img>
              <v-text-field
                v-model="section.content"
                placeholder="Enter image URL..."
                variant="plain"
                hide-details
                density="compact"
                prepend-inner-icon="mdi-link-variant"
                class="text-caption text-grey"
              ></v-text-field>
            </div>
          </div>
        </v-window-item>

        <!-- Ludography -->
        <v-window-item value="ludography">
          <div class="d-flex align-center justify-space-between mb-4">
            <h4 class="text-subtitle-2 font-weight-black">Your Games</h4>
            <v-btn prepend-icon="mdi-plus" variant="flat" color="primary" size="small" rounded="pill" @click="addGame">Add Game</v-btn>
          </div>

          <v-expansion-panels variant="accordion">
            <v-expansion-panel
              v-for="(game, index) in internalPortfolio.ludography"
              :key="'game-' + index"
              class="mb-2 bg-black-05 border border-white-05 rounded-lg overflow-hidden"
            >
              <v-expansion-panel-title>
                <v-icon icon="mdi-controller" class="mr-3" color="primary" size="small"></v-icon>
                <div class="text-subtitle-2 font-weight-bold">{{ game.title || 'Untitled Game' }}</div>
                <v-spacer></v-spacer>
                <div class="d-flex align-center mr-2">
                  <v-btn icon="mdi-arrow-up" variant="text" size="x-small" @click.stop="moveGame(index, -1)" :disabled="index === 0"></v-btn>
                  <v-btn icon="mdi-arrow-down" variant="text" size="x-small" @click.stop="moveGame(index, 1)" :disabled="index === internalPortfolio.ludography.length - 1"></v-btn>
                </div>
                <v-btn icon="mdi-delete" variant="text" size="x-small" color="error" @click.stop="removeGame(index)"></v-btn>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="d-flex align-center gap-2 mb-2">
                  <v-text-field 
                    v-model="game.title" 
                    label="Title" 
                    density="compact" 
                    variant="outlined" 
                    hide-details
                    @keydown.enter.prevent="openSearch(index)"
                  ></v-text-field>
                  <v-btn
                    color="primary"
                    prepend-icon="mdi-magnify"
                    @click="openSearch(index)"
                    text="Search in IGDB"
                    class="text-none font-weight-bold"
                    height="40"
                  ></v-btn>
                </div>
                <v-row>
                  <v-col cols="6">
                    <v-text-field v-model="game.role" label="Role (e.g. Lead Dev)" density="compact" variant="outlined"></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="game.year" label="Year" density="compact" variant="outlined"></v-text-field>
                  </v-col>
                </v-row>
                <v-text-field v-model="game.platform" label="Platforms" density="compact" variant="outlined" class="mb-2"></v-text-field>
                <v-text-field v-model="game.link" label="Project Link (Linktree, Itch.io, etc)" density="compact" variant="outlined" class="mb-2"></v-text-field>
                
                <div class="thumbnail-editor mb-2">
                  <div class="d-flex align-center justify-space-between mb-2">
                    <span class="text-caption font-weight-bold">Game Thumbnail</span>
                    <v-btn-toggle v-model="game._uploadType" mandatory density="compact" color="primary" variant="outlined" class="rounded-lg" style="height: 32px;">
                      <v-btn value="file" size="x-small">File</v-btn>
                      <v-btn value="url" size="x-small">URL</v-btn>
                    </v-btn-toggle>
                  </div>

                  <div v-if="game._uploadType === 'file'" class="d-flex align-center gap-4">
                    <v-img
                      v-if="resolveGameThumbnail(game)"
                      :src="resolveGameThumbnail(game)"
                      width="64"
                      height="64"
                      cover
                      class="rounded-lg border border-white-05 bg-black-05"
                    ></v-img>
                    <div v-else class="rounded-lg border border-white-05 bg-black-05 d-flex align-center justify-center text-grey" style="width: 64px; height: 64px;">
                      <v-icon icon="mdi-image-plus"></v-icon>
                    </div>
                    <v-btn 
                      variant="outlined" 
                      size="small" 
                      prepend-icon="mdi-upload" 
                      @click="triggerGameImageInput(index)"
                      class="text-none"
                    >
                      Upload Image
                    </v-btn>
                  </div>
                  <v-text-field 
                    v-else 
                    v-model="game.thumbnail" 
                    label="Thumbnail URL" 
                    density="compact" 
                    variant="outlined"
                    prepend-inner-icon="mdi-link-variant"
                  ></v-text-field>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <div v-if="internalPortfolio.ludography.length === 0" class="text-center py-8 text-grey text-caption border border-dashed rounded-lg border-white-05">
            No games added yet.
          </div>
        </v-window-item>

        <!-- Experience -->
        <v-window-item value="experience">
          <div class="d-flex align-center justify-space-between mb-4">
            <h4 class="text-subtitle-2 font-weight-black">Professional Experience</h4>
            <v-btn prepend-icon="mdi-plus" variant="flat" color="primary" size="small" rounded="pill" @click="addExperience">Add Work</v-btn>
          </div>

          <v-expansion-panels variant="accordion">
            <v-expansion-panel
              v-for="(exp, index) in internalPortfolio.experiences"
              :key="'exp-' + index"
              class="mb-2 bg-black-05 border border-white-05 rounded-lg overflow-hidden"
            >
              <v-expansion-panel-title>
                <v-icon icon="mdi-briefcase" class="mr-3" color="primary" size="small"></v-icon>
                <div class="text-subtitle-2 font-weight-bold">{{ exp.company || 'New Position' }}</div>
                <v-spacer></v-spacer>
                <div class="d-flex align-center mr-2">
                  <v-btn icon="mdi-arrow-up" variant="text" size="x-small" @click.stop="moveExperience(index, -1)" :disabled="index === 0"></v-btn>
                  <v-btn icon="mdi-arrow-down" variant="text" size="x-small" @click.stop="moveExperience(index, 1)" :disabled="index === internalPortfolio.experiences.length - 1"></v-btn>
                </div>
                <v-btn icon="mdi-delete" variant="text" size="x-small" color="error" @click.stop="removeExperience(index)"></v-btn>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-text-field v-model="exp.company" label="Company/Studio" density="compact" variant="outlined" class="mb-2"></v-text-field>
                <v-text-field v-model="exp.position" label="Role" density="compact" variant="outlined" class="mb-2"></v-text-field>
                <v-row>
                  <v-col cols="6">
                    <v-text-field v-model="exp.startDate" label="Start Date (e.g. 2021)" density="compact" variant="outlined"></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="exp.endDate" label="End Date" density="compact" variant="outlined" :disabled="exp.current"></v-text-field>
                  </v-col>
                </v-row>
                <v-checkbox v-model="exp.current" label="Currently working here" density="compact" color="primary" hide-details class="mb-2"></v-checkbox>
                <v-textarea v-model="exp.description" label="What did you do?" variant="outlined" density="compact" auto-grow rows="2"></v-textarea>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-window-item>

        <!-- Links & Contact -->
        <v-window-item value="links">
          <h4 class="text-subtitle-2 font-weight-black mb-4">Social Links</h4>
          <div v-for="(social, index) in internalPortfolio.socials" :key="index" class="d-flex align-center gap-2 mb-2">
            <v-text-field v-model="social.platform" placeholder="Platform (e.g. Twitter)" density="compact" variant="outlined" hide-details></v-text-field>
            <v-text-field v-model="social.url" placeholder="URL" density="compact" variant="outlined" hide-details></v-text-field>
            <v-btn icon="mdi-delete" variant="text" size="small" color="error" @click="removeSocial(index)"></v-btn>
          </div>
          <v-btn prepend-icon="mdi-plus" variant="text" color="primary" size="small" class="mb-6" @click="addSocial">Add Social Link</v-btn>

          <h4 class="text-subtitle-2 font-weight-black mb-4">Contact Information</h4>
          <v-text-field v-model="internalPortfolio.contact.email" label="Public Contact Email" density="compact" variant="outlined" prepend-inner-icon="mdi-email-outline" class="mb-2"></v-text-field>
          <v-text-field v-model="internalPortfolio.contact.website" label="Personal Website" density="compact" variant="outlined" prepend-inner-icon="mdi-web"></v-text-field>
        </v-window-item>

      </v-window>
    </v-card-text>

    <v-divider></v-divider>
    <v-card-actions class="pa-4">
      <v-spacer></v-spacer>
      <v-btn variant="text" @click="$emit('close')" :disabled="saving">Cancel</v-btn>
      <v-btn color="primary" variant="flat" rounded="pill" class="px-8 font-weight-black" :loading="saving" @click="handleSave">
        Save Portfolio
      </v-btn>
    </v-card-actions>

    <!-- IGDB Search Dialog -->
    <v-dialog v-model="searchDialog.show" max-width="500px">
      <v-card class="rounded-xl bg-editor border border-white-05">
        <v-card-title class="d-flex align-center pa-4">
          <span class="text-subtitle-1 font-weight-black">Search IGDB</span>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" size="small" @click="searchDialog.show = false"></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-text-field
            v-model="searchDialog.query"
            label="Game Title"
            variant="outlined"
            density="compact"
            prepend-inner-icon="mdi-magnify"
            autofocus
            hide-details
            class="mb-4"
            @keydown.enter="performSearch"
          ></v-text-field>
          
          <div v-if="searchDialog.loading" class="text-center py-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          
          <v-list v-else-if="searchDialog.results.length > 0" class="bg-transparent pa-0">
            <v-list-item
              v-for="result in searchDialog.results"
              :key="result.id"
              class="rounded-lg mb-2 bg-black-05 border border-white-05"
              @click="selectGame(result)"
            >
              <template v-slot:prepend>
                <v-avatar rounded="lg" size="48">
                  <v-img :src="formatCoverUrl(result.cover?.url)" cover>
                    <template v-slot:placeholder>
                      <v-icon icon="mdi-controller"></v-icon>
                    </template>
                  </v-img>
                </v-avatar>
              </template>
              <v-list-item-title class="font-weight-bold">{{ result.name }}</v-list-item-title>
              <v-list-item-subtitle v-if="result.first_release_date">
                {{ new Date(result.first_release_date * 1000).getFullYear() }}
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
          
          <div v-else-if="searchDialog.query && !searchDialog.loading" class="text-center py-4 text-grey">
            No games found.
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Feedback Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" rounded="pill">
      {{ snackbar.text }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { getPortfolio, savePortfolio, Portfolio, Experience, GameWork, SocialLink, PortfolioSection, uploadBlob, currentUser, getPdsEndpoint } from '@/services/bluesky';
import { atproto } from '@/services/atproto';
import { searchGames, formatCoverUrl, IGDBGame } from '@/services/igdb';

const emit = defineEmits(['close', 'saved']);

const activeTab = ref('bio');
const loading = ref(true);
const saving = ref(false);
const imageInput = ref<HTMLInputElement | null>(null);
const gameImageInput = ref<HTMLInputElement | null>(null);
const activeGameIndex = ref<number>(-1);

const internalPortfolio = reactive<Portfolio>({
  bio: '',
  sections: [],
  experiences: [],
  ludography: [],
  socials: [],
  contact: {},
  updatedAt: new Date().toISOString()
});

const pdsEndpoint = ref<string | null>(null);

const resolveImageUrl = (section: PortfolioSection) => {
  // 1. Pending local file (prioritized for immediate preview)
  if (section.pendingFile) {
    return section.content; // Local blob URL
  }
  // 2. Native BlueSky blob
  if (section.blob) {
    const cid = section.blob.ref?.toString() || section.blob.cid;
    const mime = section.blob.mimeType || section.blob.mime_type;
    
    if (cid && currentUser.value?.did) {
      const isGif = mime === 'image/gif' || (section.content && section.content.toLowerCase().endsWith('.gif'));
      
      // For GIFs, try direct PDS link if available, otherwise use CDN
      if (isGif && pdsEndpoint.value) {
        return `${pdsEndpoint.value}/xrpc/com.atproto.sync.getBlob?did=${currentUser.value.did}&cid=${cid}`;
      }
      
      const format = isGif ? '@gif' : '@jpeg';
      return `https://cdn.bsky.app/img/feed_fullsize/plain/${currentUser.value.did}/${cid}${format}`;
    }
  }
  // 3. Fallback to manually entered URL
  return section.content;
};

const resolveGameThumbnail = (game: any) => {
  if (game.pendingFile) {
    return game.thumbnail; // Local blob URL stored in thumbnail field temporarily
  }
  if (game.blob) {
    const cid = game.blob.ref?.toString() || game.blob.cid;
    if (cid && currentUser.value?.did) {
      return `https://cdn.bsky.app/img/feed_fullsize/plain/${currentUser.value.did}/${cid}@jpeg`;
    }
  }
  return game.thumbnail;
};

const snackbar = reactive({
  show: false,
  text: '',
  color: 'success'
});

const searchDialog = reactive({
  show: false,
  query: '',
  loading: false,
  results: [] as IGDBGame[],
  targetIndex: -1
});

const loadInitialData = async () => {
  loading.value = true;
  
  // Resolve PDS endpoint once for the user
  if (currentUser.value?.did) {
    getPdsEndpoint(currentUser.value.did).then(endpoint => {
      pdsEndpoint.value = endpoint;
    });
  }

  const data = await getPortfolio();
  if (data) {
    Object.assign(internalPortfolio, data);
    // Ensure nested objects/arrays exist
    if (!internalPortfolio.sections) internalPortfolio.sections = [];
    if (!internalPortfolio.experiences) internalPortfolio.experiences = [];
    if (!internalPortfolio.ludography) internalPortfolio.ludography = [];
    
    // Set default upload types
    internalPortfolio.ludography.forEach(game => {
      (game as any)._uploadType = game.blob ? 'file' : 'url';
    });

    if (!internalPortfolio.socials) internalPortfolio.socials = [];
    if (!internalPortfolio.contact) internalPortfolio.contact = {};
  }
  loading.value = false;
};

const handleSave = async () => {
  // Basic validation - check if at least something is filled
  if (!internalPortfolio.bio && internalPortfolio.sections.length === 0 && internalPortfolio.experiences.length === 0 && internalPortfolio.ludography.length === 0) {
    snackbar.text = 'Please fill in at least some part of your portfolio';
    snackbar.color = 'error';
    snackbar.show = true;
    return;
  }

  saving.value = true;
  try {
    // 1. Process and upload any pending images
    const processedSections = await Promise.all(internalPortfolio.sections.map(async (section) => {
      if (section.type === 'image' && section.pendingFile) {
        console.log('[Portfolio Save] Uploading pending image:', section.pendingFile.name);
        try {
          const response = await uploadBlob(section.pendingFile, section.pendingFile.type);
          // Return updated section with blob reference and clear pending state
          const { pendingFile, ...rest } = section;
          return {
            ...rest,
            blob: response,
            content: '' // Clear temporary preview URL
          };
        } catch (uploadErr) {
          console.error('[Portfolio Save] Failed to upload image block:', uploadErr);
          throw new Error('Failed to upload one or more images. Please try again.');
        }
      }
      return section;
    }));

    // 2. Process game thumbnails
    const processedGames = await Promise.all(internalPortfolio.ludography.map(async (game) => {
      if (game.pendingFile) {
        console.log('[Portfolio Save] Uploading game thumbnail:', game.pendingFile.name);
        try {
          const response = await uploadBlob(game.pendingFile, game.pendingFile.type);
          const { pendingFile, ...rest } = game;
          return {
            ...rest as any,
            blob: response,
            thumbnail: '' // Clear temporary preview URL
          };
        } catch (err) {
          console.error('[Portfolio Save] Failed to upload game thumbnail:', err);
          throw new Error('Failed to upload game thumbnail. Please try again.');
        }
      }
      return game;
    }));

    // 3. Clear any local preview URLs or file objects from the final data
    const finalPortfolio = {
      ...internalPortfolio,
      sections: processedSections.map(s => {
        const { pendingFile, ...rest } = s;
        return rest;
      }),
      ludography: processedGames.map(g => {
        const { pendingFile, _uploadType, ...rest } = g as any;
        return rest;
      })
    };

    await savePortfolio(finalPortfolio);
    snackbar.text = 'Portfolio saved successfully!';
    snackbar.color = 'success';
    snackbar.show = true;
    
    setTimeout(() => {
      emit('saved');
    }, 500);
    
    setTimeout(() => {
      emit('close');
    }, 1500);
  } catch (err) {
    snackbar.text = 'Failed to save portfolio. Please try again.';
    snackbar.color = 'error';
    snackbar.show = true;
  } finally {
    saving.value = false;
  }
};

// Bio Block Helpers
const addBlock = (type: PortfolioSection['type']) => {
  internalPortfolio.sections.push({ type, content: '' });
};

const removeBlock = (index: number) => {
  internalPortfolio.sections.splice(index, 1);
};

const onImageFileSelected = async (event: any) => {
  const file = event.target.files?.[0];
  if (!file) return;

  // Create local preview URL
  const previewUrl = URL.createObjectURL(file);
  
  // Add to sections with pendingFile for upload during save
  internalPortfolio.sections.push({ 
    type: 'image', 
    content: previewUrl,
    pendingFile: file 
  });

  snackbar.text = 'Image added to bio.';
  snackbar.color = 'info';
  snackbar.show = true;

  // Clear input
  if (imageInput.value) imageInput.value.value = '';
};

const moveBlock = (index: number, direction: number) => {
  const newIndex = index + direction;
  if (newIndex < 0 || newIndex >= internalPortfolio.sections.length) return;
  const temp = internalPortfolio.sections[index];
  internalPortfolio.sections[index] = internalPortfolio.sections[newIndex];
  internalPortfolio.sections[newIndex] = temp;
};

// Ludography Helpers
const addGame = () => {
  internalPortfolio.ludography.unshift({
    title: '',
    role: '',
    year: new Date().getFullYear().toString(),
    platform: '',
    _uploadType: 'url'
  } as any);
};

const removeGame = (index: number) => {
  internalPortfolio.ludography.splice(index, 1);
};

const moveGame = (index: number, direction: number) => {
  const newIndex = index + direction;
  if (newIndex < 0 || newIndex >= internalPortfolio.ludography.length) return;
  const temp = internalPortfolio.ludography[index];
  internalPortfolio.ludography[index] = internalPortfolio.ludography[newIndex];
  internalPortfolio.ludography[newIndex] = temp;
};

// IGDB Search Helpers
const openSearch = (index: number) => {
  searchDialog.targetIndex = index;
  searchDialog.query = internalPortfolio.ludography[index].title;
  searchDialog.results = [];
  searchDialog.show = true;
  if (searchDialog.query) {
    performSearch();
  }
};

const performSearch = async () => {
  if (!searchDialog.query) return;
  searchDialog.loading = true;
  try {
    searchDialog.results = await searchGames(searchDialog.query);
    if (searchDialog.results.length === 0) {
      console.warn('[IGDB] No results found for query:', searchDialog.query);
    }
  } catch (err) {
    console.error('[IGDB] Search failed:', err);
    snackbar.text = 'IGDB search failed. Check your API keys and connection.';
    snackbar.color = 'error';
    snackbar.show = true;
  } finally {
    searchDialog.loading = false;
  }
};

const selectGame = (game: IGDBGame) => {
  const index = searchDialog.targetIndex;
  if (index === -1) return;

  const target = internalPortfolio.ludography[index];
  target.title = game.name;
  if (game.first_release_date) {
    target.year = new Date(game.first_release_date * 1000).getFullYear().toString();
  }
  if (game.platforms) {
    target.platform = game.platforms.map(p => p.name).join(', ');
  }
  if (game.cover) {
    target.thumbnail = formatCoverUrl(game.cover.url);
  }
  if (game.url) {
    target.link = game.url;
  }

  searchDialog.show = false;
};

// Game Image Helpers
const triggerGameImageInput = (index: number) => {
  activeGameIndex.value = index;
  gameImageInput.value?.click();
};

const onGameImageFileSelected = (event: any) => {
  const file = event.target.files?.[0];
  if (!file || activeGameIndex.value === -1) return;

  const previewUrl = URL.createObjectURL(file);
  const game = internalPortfolio.ludography[activeGameIndex.value];
  game.thumbnail = previewUrl;
  game.pendingFile = file;

  // Clear input
  if (gameImageInput.value) gameImageInput.value.value = '';
};

// Experience Helpers
const addExperience = () => {
  internalPortfolio.experiences.unshift({
    company: '',
    position: '',
    startDate: '',
    current: true,
    description: ''
  });
};

const removeExperience = (index: number) => {
  internalPortfolio.experiences.splice(index, 1);
};

const moveExperience = (index: number, direction: number) => {
  const newIndex = index + direction;
  if (newIndex < 0 || newIndex >= internalPortfolio.experiences.length) return;
  const temp = internalPortfolio.experiences[index];
  internalPortfolio.experiences[index] = internalPortfolio.experiences[newIndex];
  internalPortfolio.experiences[newIndex] = temp;
};

// Social Helpers
const addSocial = () => {
  internalPortfolio.socials.push({ platform: '', url: '' });
};

const removeSocial = (index: number) => {
  internalPortfolio.socials.splice(index, 1);
};


onMounted(async () => {
  await loadInitialData();
});
</script>

<style scoped>
.portfolio-editor {
  max-width: 600px;
}

.bg-editor {
  background: #121212 !important;
}

.bg-black-05 {
  background: rgba(255, 255, 255, 0.05) !important;
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

.gap-2 { gap: 8px; }

.block-editor {
  transition: all 0.2s ease;
}

.block-editor:hover {
  border-color: rgba(59, 130, 246, 0.5) !important;
}

:deep(.v-expansion-panel-text__wrapper) {
  padding: 16px !important;
}
</style>
